import * as React from "react";

import { NOTES } from "../../../reducers/ui/reasoningTool";

import classNames from "classnames";
import generateUUID from "../../../helpers/generateUUID";
import { getAriaLabel } from "./iconButtonHelpers";

interface DocNoteProps {
  noteUid?: string;
  uid: string;
  displayContext: DisplayContext;
  userContext: UserContext & { notes?: { [key: string]: any } };
  content?: any; // InlineNode - can be string or styled text object
  /** Block node type (paragraph, regularList, image, …) for accessible name fallback */
  type?: string;
  /** Media blocks use `title` instead of `content` for caption text */
  title?: any;
}

export default function DocNote(props: DocNoteProps) {
  const {
    displayContext,
    noteUid,
    uid,
    userContext,
    content,
    type,
    title,
  } = props;

  if (displayContext.canBeEditing || displayContext.isEmbeded) { return null; }

  const onClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (noteUid) {
      userContext.deleteNote(noteUid);
      displayContext.setFocusedBookmarkUid(null);
    } else {
      const newUid = generateUUID();
      userContext.addNote(newUid, {notedNodeUid: uid});

      // Store which element triggered the modal so we can return focus to it
      if (noteButtonRef.current) {
        // Store the ref globally so Panel can access it for focus return
        (window as any).__lastNotesTriggerElement = noteButtonRef.current;
      }

      displayContext.togglePanel(NOTES);
      displayContext.panelNodeEditStart(newUid);
      displayContext.setFocusedBookmarkUid(uid);
    }

    e.stopPropagation();
  };

  const noteButtonRef = React.useRef<HTMLButtonElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Escape") {
      noteButtonRef.current?.blur();
      e.stopPropagation();
    }
  };



  // Remove .noted class if noteUid is not present (defensive)
  const isNoted = !!noteUid && userContext?.notes?.[noteUid];

  return (
    <button
      type="button"
      id={`note${uid}`}
      className={classNames("doc-note-button", {noted: isNoted})}
      onClick={onClick}
      aria-pressed={!!noteUid}
      aria-label={getAriaLabel(content, isNoted, "Remove note about", "Add note about", {
        title,
        type,
        uid,
      })}
      ref={noteButtonRef}
      onKeyDown={handleKeyDown}
    >
      <span className="icon-notes" aria-hidden="true" />
    </button>
  );
}
